/**
 * Firmware Tab — merged Discover + Devices view.
 *
 * Phase 5 REDESIGN (2026-07-25). Mirrors the Apps Management Discover
 * page (`frontend/src/pages/DiscoverPage.jsx` lines ~2336–2610):
 *   • Lighter blue Card / row styling — glass panel + row hover.
 *   • Coloured next-action buttons per packaging_status.
 *   • OEM Catalogue strip (moved from FirmwareReportTab).
 *   • Model-scoped KPI tiles (on-latest / ahead / missing-1 / missing-multi / unknown).
 *   • Collapsed Compliance-by-manufacturer chart.
 *   • Model families table (no Criticality column).
 *   • Model Governance drill-in modal (right-side slide-over, mirrors
 *     AppGovernancePage.jsx sections: Owner, Testers, Estate Overview,
 *     Deployment Status, Version Distribution).
 *   • Custom Upload modal — admin drops a .exe BIOS installer that
 *     ALICE packages + deploys without the OEM catalogue path.
 *
 * Backing endpoints:
 *   GET   /api/os-patch/firmware/families
 *   GET   /api/os-patch/firmware/families/{key}
 *   GET   /api/os-patch/firmware/families/{key}/devices
 *   PATCH /api/os-patch/firmware/families/{key}
 *   GET   /api/os-patch/firmware/families/{key}/governance
 *   PUT   /api/os-patch/firmware/families/{key}/governance
 *   POST  /api/os-patch/firmware/families/{key}/package
 *   POST  /api/os-patch/firmware/families/{key}/custom-upload  (multipart)
 *   POST  /api/os-patch/firmware/families/{key}/promote-approved
 *   POST  /api/os-patch/firmware/families/refresh-index
 *   POST  /api/os-patch/firmware/auto-update/run
 *   GET   /api/os-patch/firmware/families-summary-counts
 *   POST  /api/os-patch/firmware/deployments/{id}/advance-phase
 *   POST  /api/os-patch/firmware/deployments/{id}/go-live
 *   GET   /api/os-patch/firmware/catalog/sync-status
 *   POST  /api/os-patch/firmware/catalog/sync
 *   POST  /api/os-patch/firmware/catalog/sync/{oem}
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Cpu, RefreshCw, Search, EyeOff, Eye, Rocket, ChevronRight, ChevronDown,
  Settings2, X, Package, CheckCircle2, AlertTriangle, Upload, Download,
  HardDrive, Shield, Users, User, Mail, Save, Layers, FileText, Plus,
  ArrowLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import FirmwareDevicesSection from './FirmwareDevicesSection';
import { usePermissions } from '@/contexts/PermissionsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/os-patch`;

// ─────────────────────────── OEM display labels ─────────────────────
const OEM_LABELS = {
  lenovo: 'Lenovo', hp: 'HP', dell: 'Dell',
  acer: 'Acer (manual)', microsoft: 'Microsoft Surface',
  samsung: 'Samsung (manual)',
};

// Pretty-print "5m ago" / "2h ago"
const _timeAgo = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '—';
  const diff = Date.now() - t.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

// A Custom Upload BIOS saved on the model that hasn't been packaged yet.
const hasStagedCustom = (family) => Boolean(
  family?.custom_payload && !family.custom_payload.packaged_deployment_id,
);

// ─────────────────────────── generic collapsible section ────────────
function CollapsibleSection({ title, icon: Icon, children, defaultOpen = false, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]" data-testid={testId}>
      <CardHeader
        className={`cursor-pointer select-none hover:bg-[rgba(229,239,250,0.05)] transition-colors ${open ? 'border-b border-[rgba(229,239,250,0.1)]' : ''}`}
        onClick={() => setOpen((v) => !v)}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <CardTitle className="text-white flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-cyan-400" />}
            {title}
          </span>
          <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${open ? 'rotate-180' : ''}`} />
        </CardTitle>
      </CardHeader>
      {open && <CardContent className="p-4">{children}</CardContent>}
    </Card>
  );
}

// ─────────────────────────── KPI tile (bucket, families+devices) ────
function ModelKPICard({ label, families, devices, subtitle, icon: Icon, accentColor, active, testId }) {
  return (
    <div
      className={`p-4 rounded-lg border transition-all cursor-pointer backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] ${
        active
          ? 'bg-[rgba(229,239,250,0.3)] border-cyan-400 shadow-lg shadow-cyan-500/20'
          : 'bg-[rgba(229,239,250,0.2)] border-[rgba(229,239,250,0.1)] hover:bg-[rgba(229,239,250,0.28)]'
      }`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="text-white/80 text-sm font-medium">{label}</div>
        {Icon && <Icon className="h-4 w-4" style={{ color: accentColor }} />}
      </div>
      <div className="text-2xl font-bold tabular-nums" style={{ color: accentColor }}>
        {families?.toLocaleString?.() || 0}
        <span className="text-white/60 text-sm font-normal"> famil{families === 1 ? 'y' : 'ies'}</span>
      </div>
      <div className="text-lg font-semibold text-white/90 tabular-nums mt-0.5">
        {devices?.toLocaleString?.() || 0}
        <span className="text-white/60 text-xs font-normal ml-1">device{devices === 1 ? '' : 's'}</span>
      </div>
      {subtitle && <div className="text-xs text-white/50 mt-1">{subtitle}</div>}
    </div>
  );
}

// ─────────────────────────── Top card (Total Models / Versions / Ready to Package) ────
function TopStatCard({ label, value, subtitle, icon: Icon, accentColor, testId }) {
  return (
    <div
      className="p-5 rounded-lg border bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]"
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="text-white/80 text-sm font-medium">{label}</div>
        {Icon && <Icon className="h-5 w-5" style={{ color: accentColor }} />}
      </div>
      <div className="text-4xl font-bold tabular-nums" style={{ color: accentColor }}>
        {value?.toLocaleString?.() || 0}
      </div>
      {subtitle && <div className="text-xs text-white/60 mt-1.5">{subtitle}</div>}
    </div>
  );
}


// ─────────────────────────── phase config modal (4-phase, hard-coded) ────
function PhaseConfigModal({ family, onClose, onSaved }) {
  const [ownersText, setOwnersText] = useState(
    (family?.phase_config?.owner_emails || []).join(', '),
  );
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const emails = ownersText.split(/[,\s;]+/).map((s) => s.trim()).filter(Boolean);
      const patch = { ...(family.phase_config || {}), owner_emails: emails };
      const res = await axios.patch(
        `${API}/firmware/families/${family.family_key}`,
        { phase_config: patch },
      );
      toast.success('Phase config saved');
      onSaved(res.data);
      onClose();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };
  if (!family) return null;
  const testerCount = (family.testers || []).length;
  const deviceCount = family.device_count || 0;
  const p10 = Math.max(1, Math.ceil(deviceCount * 0.10));
  const p50 = Math.max(1, Math.ceil(deviceCount * 0.50));
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="phase-config-modal">
      <div className="alice-popup bg-slate-900 rounded-lg border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <h3 className="text-lg font-semibold text-white">Phased Deployment Config</h3>
            <p className="text-xs text-white/60">{family.manufacturer} · {family.model_family}</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="phase-config-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-xs text-white/60">
            Firmware follows a fixed 4-phase rollout. ALICE auto-creates the target Entra ID group
            (naming convention <code className="text-cyan-300">ALICE-app-{'{Mfr}'}-{'{Model}'}-{'{Version}'}-Phase-{'{...}'}</code>)
            and picks the devices for each phase. Add tester emails under
            &ldquo;Model Governance&rdquo; on the family row to populate the Test phase.
          </p>
          <div className="space-y-2" data-testid="phase-config-preview">
            <div className="flex items-center justify-between p-2.5 rounded bg-slate-800/60 border border-white/10">
              <div>
                <div className="text-white text-sm font-semibold">Test</div>
                <div className="text-xs text-white/60">Tester primary devices (Model Governance)</div>
              </div>
              <Badge className="bg-purple-600 text-white">{testerCount} tester{testerCount === 1 ? '' : 's'}</Badge>
            </div>
            <div className="flex items-center justify-between p-2.5 rounded bg-slate-800/60 border border-white/10">
              <div>
                <div className="text-white text-sm font-semibold">Phase 1 — 10%</div>
                <div className="text-xs text-white/60">≈ {p10.toLocaleString()} device{p10 === 1 ? '' : 's'}</div>
              </div>
              <Badge className="bg-blue-600 text-white">10%</Badge>
            </div>
            <div className="flex items-center justify-between p-2.5 rounded bg-slate-800/60 border border-white/10">
              <div>
                <div className="text-white text-sm font-semibold">Phase 2 — 50%</div>
                <div className="text-xs text-white/60">≈ {p50.toLocaleString()} device{p50 === 1 ? '' : 's'}</div>
              </div>
              <Badge className="bg-blue-600 text-white">50%</Badge>
            </div>
            <div className="flex items-center justify-between p-2.5 rounded bg-slate-800/60 border border-white/10">
              <div>
                <div className="text-white text-sm font-semibold">Phase 3 — 100%</div>
                <div className="text-xs text-white/60">All remaining devices in the family</div>
              </div>
              <Badge className="bg-green-600 text-white">100%</Badge>
            </div>
          </div>
          <label className="block text-sm text-white/80 pt-2">
            <span className="block mb-1">Phase-transition notification emails (comma-separated)</span>
            <input
              type="text"
              value={ownersText}
              onChange={(e) => setOwnersText(e.target.value)}
              placeholder="alice@camwood.com, bob@camwood.com"
              className="w-full px-2 py-1.5 bg-slate-800/60 border border-white/10 rounded text-sm text-white placeholder-white/30 focus:outline-none focus:border-cyan-400"
              data-testid="phase-config-owners"
            />
            <span className="block mt-1 text-[11px] text-white/50">
              These emails are notified when the deployment advances a phase, or when a tester defers via PSADT.
            </span>
          </label>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-white/10">
          <Button variant="outline" onClick={onClose} className="text-white/80 border-white/20" data-testid="phase-config-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="phase-config-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── custom upload modal ──────────────────────
// 2026-09-16 (Bug 3) — Custom Upload only STAGES the BIOS (file or
// download URL) on the model. Nothing is deployed until Package is pressed,
// so Model Owner / Testers can be set first and the payload goes through the
// normal Test → Phase 1/2/3 rollout with the standard phase group names.
function CustomUploadModal({ family, onClose, onUploaded }) {
  const [mode, setMode] = useState('file'); // 'file' | 'url'
  const [file, setFile] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [targetVersion, setTargetVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [msConfirm, setMsConfirm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const isMicrosoft = (family?.manufacturer || '').toLowerCase().startsWith('microsoft');
  const urlValue = downloadUrl.trim();
  const hasPayload = mode === 'file' ? Boolean(file) : Boolean(urlValue);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === 'file') {
      if (!file) { toast.error('Please choose a BIOS .exe file'); return; }
      if (!file.name.toLowerCase().endsWith('.exe')) { toast.error('Only .exe payloads accepted'); return; }
    } else {
      if (!/^https:\/\/\S+$/i.test(urlValue)) { toast.error('Enter a full https:// download link'); return; }
      if (!urlValue.split('?')[0].toLowerCase().endsWith('.exe')) { toast.error('The download link must point at a .exe installer'); return; }
    }
    if (!targetVersion.trim()) { toast.error('Enter a target version'); return; }
    if (isMicrosoft && !msConfirm) {
      toast.error('Microsoft Surface: confirm the ms_confirm checkbox before uploading.');
      return;
    }

    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      if (mode === 'file') fd.append('file', file);
      else fd.append('download_url', urlValue);
      fd.append('target_version', targetVersion.trim());
      if (notes) fd.append('notes', notes);
      if (isMicrosoft) fd.append('ms_confirm_accepted', 'true');
      const res = await axios.post(
        `${API}/firmware/families/${family.family_key}/custom-upload`,
        fd,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (evt) => {
            if (mode === 'file' && evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
          },
        },
      );
      const staged = res.data?.custom_payload || {};
      const sizeText = staged.size_bytes ? ` (${(staged.size_bytes / (1024 * 1024)).toFixed(1)} MB)` : '';
      toast.success(`Custom BIOS ${staged.target_version || targetVersion.trim()} saved${sizeText}. Set testers, then press Package to start the Test phase.`);
      onUploaded?.(res.data);
      onClose();
    } catch (err) {
      toast.error(`Upload failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUploading(false);
    }
  };

  if (!family) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="custom-upload-modal">
      <form onSubmit={submit} className="alice-popup bg-slate-900 rounded-lg border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-400" /> Custom BIOS Upload
            </h3>
            <p className="text-xs text-white/60">{family.manufacturer} · {family.model_family}</p>
          </div>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white" data-testid="custom-upload-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-xs text-white/60">
            Add a BIOS installer (.exe) for this model, either as a file or a download link. Nothing is deployed yet:
            set the Model Owner and Testers, then press <b>Package</b> to start the Test phase and the phased rollout.
            {family.custom_payload && !family.custom_payload.packaged_deployment_id && (
              <span className="block mt-1 text-amber-300">
                This replaces the BIOS already saved for this model (v{family.custom_payload.target_version}).
              </span>
            )}
          </p>
          <div className="flex gap-1 p-1 rounded bg-slate-800/60 border border-white/10 w-fit" role="tablist">
            {[['file', 'Upload file'], ['url', 'Download URL']].map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={mode === value}
                onClick={() => setMode(value)}
                className={`px-3 py-1 rounded text-xs font-medium ${mode === value ? 'bg-cyan-600 text-white' : 'text-white/70 hover:text-white'}`}
                data-testid={`custom-upload-mode-${value}`}
              >
                {label}
              </button>
            ))}
          </div>
          {mode === 'file' ? (
            <div>
              <Label className="text-white/90">BIOS installer (.exe)</Label>
              <input
                type="file"
                accept=".exe"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1 block w-full text-sm text-white file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-cyan-600 file:text-white hover:file:bg-cyan-700 bg-slate-800/60 border border-white/10 rounded p-2"
                data-testid="custom-upload-file"
              />
              {file && (
                <p className="text-xs text-white/70 mt-1">
                  {file.name} · {(file.size / (1024 * 1024)).toFixed(1)} MB
                </p>
              )}
            </div>
          ) : (
            <div>
              <Label className="text-white/90">Download URL (.exe)</Label>
              <Input
                type="url"
                value={downloadUrl}
                onChange={(e) => setDownloadUrl(e.target.value)}
                placeholder="https://dl.dell.com/…/Latitude_5430_1.40.0.exe"
                className="mt-1 bg-slate-800/60 border-white/10 text-white"
                data-testid="custom-upload-url"
              />
              <p className="text-[11px] text-white/50 mt-1">
                ALICE downloads the installer from this link when each phase is packaged. It must be a public https link.
              </p>
            </div>
          )}
          <div>
            <Label className="text-white/90">Target BIOS version</Label>
            <Input
              value={targetVersion}
              onChange={(e) => setTargetVersion(e.target.value)}
              placeholder="e.g. 1.23.0"
              className="mt-1 bg-slate-800/60 border-white/10 text-white"
              data-testid="custom-upload-target-version"
            />
          </div>
          <div>
            <Label className="text-white/90">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything downstream admins should know…"
              className="mt-1 bg-slate-800/60 border-white/10 text-white min-h-[70px]"
              data-testid="custom-upload-notes"
            />
          </div>
          {isMicrosoft && (
            <label className="flex items-start gap-2 text-sm text-amber-300 bg-amber-900/20 border border-amber-500/30 rounded p-3">
              <input
                type="checkbox"
                checked={msConfirm}
                onChange={(e) => setMsConfirm(e.target.checked)}
                className="mt-0.5 accent-amber-500"
                data-testid="custom-upload-ms-confirm"
              />
              <span>
                <strong>Microsoft Surface:</strong> Surface firmware is a driver+firmware bundle.
                Microsoft recommends Windows Update + Intune driver/firmware profiles instead.
                Confirm you accept the risk of packaging it via ALICE.
              </span>
            </label>
          )}
          {uploading && (
            <div className="w-full bg-slate-800 rounded h-2 overflow-hidden">
              <div className="bg-cyan-500 h-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-white/10">
          <Button type="button" variant="outline" onClick={onClose} disabled={uploading} className="text-white/80 border-white/20" data-testid="custom-upload-cancel">
            Cancel
          </Button>
          <Button type="submit" disabled={uploading || !hasPayload || !targetVersion.trim()} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="custom-upload-submit">
            {uploading ? (mode === 'file' ? `Uploading… ${progress}%` : 'Saving…') : 'Save custom BIOS'}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────── device list modal (versions drill-in) ────
function DeviceListModal({ family, version, onClose }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const params = version ? { version } : {};
        const res = await axios.get(`${API}/firmware/families/${family.family_key}/devices`, { params });
        if (mounted) setDevices(res.data.devices || []);
      } catch (e) {
        if (mounted) toast.error(`Failed to load devices: ${e.message}`);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [family.family_key, version]);
  // Download through axios rather than window.open(): a new tab can't carry
  // the bearer token, and since 20 Oct 2026 every OS Patch route needs one.
  // (Before then this path was exempt from tenant checks, so the tab was
  // answered from the central database -- the security incident.) The App
  // interceptor adds the token and tenant header.
  const exportCsv = async () => {
    try {
      const params = version ? { version, export: 'csv' } : { export: 'csv' };
      const response = await axios.get(`${API}/firmware/families/${family.family_key}/devices`, {
        params,
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${family.family_key}-devices.csv`.replace(/::/g, '-');
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      toast.error(`Export failed: ${error.response?.status === 401 ? 'please sign in again' : error.message}`);
    }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="device-list-modal">
      <div className="alice-popup bg-slate-900 rounded-lg border border-white/10 max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <h3 className="text-lg font-semibold text-white">
              {family.manufacturer} · {family.model_family}
              {version && <span className="text-cyan-400 ml-2">v{version}</span>}
            </h3>
            <p className="text-xs text-white/60">{devices.length} device{devices.length === 1 ? '' : 's'}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={exportCsv} className="text-white/80 border-white/20" data-testid="device-list-export-csv">
              <Download className="h-4 w-4 mr-1" />CSV
            </Button>
            <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="device-list-close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="text-white/60 text-center py-6">Loading…</div>
          ) : devices.length === 0 ? (
            <div className="text-white/60 text-center py-6">No devices match.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white/70 border-b border-white/10">
                  <th className="py-2 pr-4">Device</th>
                  <th className="py-2 pr-4">Serial</th>
                  <th className="py-2 pr-4">Current BIOS</th>
                  <th className="py-2 pr-4">Last sync</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d, i) => (
                  <tr key={d.id || i} className="border-b border-white/5" data-testid={`device-list-row-${i}`}>
                    <td className="py-2 pr-4 text-white">{d.device_name || '—'}</td>
                    <td className="py-2 pr-4 text-white/70">{d.serial_number || '—'}</td>
                    <td className="py-2 pr-4 text-white/80 font-mono text-xs">{d.bios_version || '—'}</td>
                    <td className="py-2 pr-4 text-white/60 text-xs">{d.last_sync || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── model family drill-in (detail page) ────
// 2026-07-30 (Phase 5 bugs, Task A): converted from the dark slate-950
// right slide-over to an in-place detail page — same page-down pattern
// Apps Management > Discover uses (AppGovernancePage.jsx), rendered
// INSTEAD of the Firmware list with a "Back to Firmware" header and
// the blue glass card styling the rest of the OS Patch pages use.
function ModelFamilyDetailPage({ familyKey, onBack, onDeploy, onUploaded }) {
  const [gov, setGov] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [testers, setTesters] = useState([]);
  const [newTester, setNewTester] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [drillVersion, setDrillVersion] = useState(null);
  const [showCustomUpload, setShowCustomUpload] = useState(false);
  // Read-only OS Patch tenants: governance is shown as information only
  // (no owner/tester/notes editing, no Save, no Package / Custom Upload).
  const { canWriteOSPatch } = usePermissions();

  const fetchGov = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/firmware/families/${familyKey}/governance`);
      setGov(res.data);
      const g = res.data.governance || {};
      setOwnerEmail(g.owner_email || '');
      setTesters(g.testers || []);
      setNotes(g.notes || '');
    } catch (e) {
      toast.error(`Failed to load governance: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [familyKey]);
  useEffect(() => { fetchGov(); }, [fetchGov]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/firmware/families/${familyKey}/governance`, {
        owner_email: ownerEmail, testers, notes,
      });
      toast.success('Governance saved');
      fetchGov();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const clearCustom = async () => {
    if (!window.confirm('Discard the saved custom BIOS for this model?')) return;
    try {
      await axios.delete(`${API}/firmware/families/${familyKey}/custom-payload`);
      toast.success('Custom BIOS cleared');
      fetchGov();
      onUploaded?.();
    } catch (e) {
      toast.error(`Clear failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const addTester = () => {
    const v = newTester.trim().toLowerCase();
    if (v && !testers.includes(v)) {
      setTesters([...testers, v]);
      setNewTester('');
    }
  };
  const removeTester = (t) => setTesters(testers.filter((x) => x !== t));

  const family = gov?.family;
  const estate = gov?.estate_overview || {};
  const versionDist = gov?.version_distribution || [];
  const deployStatus = gov?.deployment_status || {};

  return (
    <>
      <div className="space-y-5" data-testid="firmware-family-detail-page">
        {/* Back navigation — mirrors AppGovernancePage's header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-white/90 hover:text-white"
            data-testid="firmware-family-detail-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Firmware
          </Button>
        </div>

        {/* Family header card — blue glass styling */}
        <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
          <CardContent className="py-5">
            <h3 className="text-xl font-semibold text-white flex items-center gap-2">
              <Cpu className="h-5 w-5 text-cyan-400" />
              {family ? `${family.manufacturer} · ${family.model_family}` : 'Model Governance'}
            </h3>
            <p className="text-sm text-white/60 mt-1">
              {family?.device_count?.toLocaleString?.() || 0} devices ·
              {' '}Catalogue latest: <span className="text-white/80">{estate.catalogue_latest || '—'}</span>
            </p>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center text-white/60 py-16">Loading…</div>
        ) : !gov ? (
          <div className="flex items-center justify-center text-white/60 py-16">Family not found.</div>
        ) : (
          <div className="space-y-5">
            {/* Two-column layout (2026-07-30, Task A restyle): Ownership &
                Testing on the LEFT beneath the family heading, Estate
                Overview top-RIGHT with Deployment Status below it —
                mirrors Apps Management > Discover > family drill-in.
                Lighter blue glass boxes to match AppGovernancePage. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
              {/* Left column: Owner + Testers */}
              <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
                <CardHeader className="pb-2"><CardTitle className="text-white text-sm flex items-center gap-2"><Users className="h-4 w-4 text-cyan-400" />Ownership &amp; Testing</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-white/90 text-xs flex items-center gap-1"><User className="h-3 w-3" />Model Owner (email)</Label>
                    {canWriteOSPatch ? (
                      <Input
                        value={ownerEmail}
                        onChange={(e) => setOwnerEmail(e.target.value)}
                        placeholder="owner@company.com"
                        className="mt-1 bg-slate-800/60 border-white/10 text-white"
                        data-testid="drawer-owner-email"
                      />
                    ) : (
                      <div className="mt-1 text-sm text-white/90 break-all" data-testid="drawer-owner-email-readonly">
                        {ownerEmail || <span className="text-white/50">Not set</span>}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-white/90 text-xs flex items-center gap-1"><Mail className="h-3 w-3" />Model Testers</Label>
                    {canWriteOSPatch && (
                      <div className="flex gap-2 mt-1">
                        <Input
                          value={newTester}
                          onChange={(e) => setNewTester(e.target.value)}
                          placeholder="Add tester email"
                          className="bg-slate-800/60 border-white/10 text-white"
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTester(); } }}
                          data-testid="drawer-new-tester"
                        />
                        <Button type="button" onClick={addTester} variant="outline" size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white border-none" data-testid="drawer-add-tester">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                    {!canWriteOSPatch && testers.length === 0 && (
                      <div className="mt-1 text-sm text-white/50" data-testid="drawer-testers-readonly-empty">
                        No testers configured.
                      </div>
                    )}
                    {testers.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {testers.map((t) => (
                          <Badge key={t} className="bg-slate-700 text-white pl-2 pr-1 py-1 text-xs">
                            {t}
                            {canWriteOSPatch && (
                              <button onClick={() => removeTester(t)} className="ml-1 hover:text-red-400" data-testid={`drawer-remove-tester-${t}`}>
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-white/90 text-xs flex items-center gap-1"><FileText className="h-3 w-3" />Notes</Label>
                    {canWriteOSPatch ? (
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Notes about this model family…"
                        className="mt-1 bg-slate-800/60 border-white/10 text-white min-h-[70px]"
                        data-testid="drawer-notes"
                      />
                    ) : (
                      <div className="mt-1 text-sm text-white/90 whitespace-pre-wrap break-words" data-testid="drawer-notes-readonly">
                        {notes || <span className="text-white/50">No notes</span>}
                      </div>
                    )}
                  </div>
                  {canWriteOSPatch && (
                    <div className="flex justify-end">
                      <Button onClick={save} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="drawer-save">
                        <Save className="h-4 w-4 mr-2" />
                        {saving ? 'Saving…' : 'Save'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Right column: Estate Overview with Deployment Status below */}
              <div className="space-y-5">
                <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
                  <CardHeader className="pb-2"><CardTitle className="text-white text-sm flex items-center gap-2"><HardDrive className="h-4 w-4 text-cyan-400" />Estate Overview</CardTitle></CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-white/70">Devices</span><span className="text-white font-medium">{estate.device_count?.toLocaleString?.() || 0}</span></div>
                    <div className="flex justify-between"><span className="text-white/70">Distinct BIOS versions</span><span className="text-white font-medium">{estate.distinct_versions || 0}</span></div>
                    <div className="flex justify-between"><span className="text-white/70">Oldest in estate</span><span className="text-white/90 font-mono text-xs">{estate.oldest_version || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-white/70">Latest seen</span><span className="text-white/90 font-mono text-xs">{estate.latest_seen_version || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-white/70">Catalogue latest</span><span className="text-cyan-300 font-mono text-xs">{estate.catalogue_latest || '—'}</span></div>
                    {!family?.is_virtual && estate.catalogue_latest && !(family?.latest_download_url || '').trim() && (
                      <div className="flex items-center gap-1 text-amber-400 text-xs" data-testid="drawer-no-url-hint">
                        <AlertTriangle className="h-3 w-3" />No download URL in OEM feed{canWriteOSPatch && ' — use Custom Upload'}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
                  <CardHeader className="pb-2"><CardTitle className="text-white text-sm flex items-center gap-2"><Rocket className="h-4 w-4 text-cyan-400" />Deployment Status</CardTitle></CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-white/70">Packaging</span>
                      <PackagingStatusBadge status={family?.is_virtual ? 'unsupported' : deployStatus.packaging_status?.status} />
                    </div>
                    <div className="flex justify-between"><span className="text-white/70">Latest approved</span><span className="text-green-400 font-mono text-xs">{deployStatus.latest_approved_version || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-white/70">Phase chain</span><span className="text-white/90">{deployStatus.parent_chain?.length || 0} step{deployStatus.parent_chain?.length === 1 ? '' : 's'}</span></div>
                    {family?.custom_payload && (
                      <div className="mt-1 p-2 rounded border border-blue-500/30 bg-blue-900/20 text-xs space-y-1" data-testid="drawer-custom-payload">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-blue-200 font-medium flex items-center gap-1">
                            <Upload className="h-3 w-3" />Custom BIOS v{family.custom_payload.target_version}
                          </span>
                          <span className="text-white/60">
                            {family.custom_payload.packaged_deployment_id ? 'Packaged' : 'Ready to package'}
                          </span>
                        </div>
                        <div className="text-white/70 break-all">
                          {family.custom_payload.source === 'url'
                            ? family.custom_payload.download_url
                            : `${family.custom_payload.filename || 'installer'}${family.custom_payload.size_bytes ? ` · ${(family.custom_payload.size_bytes / (1024 * 1024)).toFixed(1)} MB` : ''}`}
                        </div>
                        {!family.custom_payload.packaged_deployment_id && (
                          <div className="text-white/50">
                            Set the Model Owner and Testers, then press Package to start the Test phase.
                          </div>
                        )}
                      </div>
                    )}
                    <div className="pt-2 flex gap-2 flex-wrap">
                      {family?.is_virtual ? (
                        <Badge variant="outline" className="text-slate-300 border-slate-500/50 bg-slate-700/30 text-xs" data-testid="drawer-unsupported-badge">
                          Unsupported — Virtual Machine / Cloud PC
                        </Badge>
                      ) : canWriteOSPatch ? (
                        <>
                          {hasStagedCustom(family) && (
                            <Button size="sm" onClick={() => onDeploy?.(family, 'custom')} className="bg-green-500 hover:bg-green-600 text-white text-xs" data-testid="drawer-deploy-custom-btn">
                              <Package className="h-3 w-3 mr-1" />Package custom v{family.custom_payload.target_version}
                            </Button>
                          )}
                          {family?.latest_available_version && family?.latest_download_url && (
                            <Button size="sm" onClick={() => onDeploy?.(family, 'catalogue')} className={`${hasStagedCustom(family) ? 'bg-slate-600 hover:bg-slate-700' : 'bg-green-500 hover:bg-green-600'} text-white text-xs`} data-testid="drawer-deploy-btn">
                              <Package className="h-3 w-3 mr-1" />{hasStagedCustom(family) ? `Package catalogue v${family.latest_available_version}` : 'Package now'}
                            </Button>
                          )}
                          {hasStagedCustom(family) && (
                            <Button size="sm" variant="outline" onClick={clearCustom} className="text-white/80 border-white/20 text-xs" data-testid="drawer-clear-custom-btn">
                              <X className="h-3 w-3 mr-1" />Clear custom BIOS
                            </Button>
                          )}
                          <Button size="sm" onClick={() => setShowCustomUpload(true)} className="bg-blue-500 hover:bg-blue-600 text-white text-xs" data-testid="drawer-custom-upload-btn">
                            <Upload className="h-3 w-3 mr-1" />Custom Upload
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Version distribution — full width at the bottom */}
            <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
              <CardHeader className="pb-2"><CardTitle className="text-white text-sm flex items-center gap-2"><Layers className="h-4 w-4 text-cyan-400" />Version Distribution ({versionDist.length})</CardTitle></CardHeader>
              <CardContent>
                {versionDist.length === 0 ? (
                  <button
                    onClick={() => setDrillVersion('__all__')}
                    className="w-full flex items-center justify-between py-1.5 hover:bg-white/5 rounded px-2 text-left"
                    title="View the devices in this family"
                    data-testid="drawer-version-none"
                  >
                    <span className="text-white/70 text-sm">
                      No BIOS versions reported yet — view {estate.device_count ?? 0} device{(estate.device_count ?? 0) === 1 ? '' : 's'}
                    </span>
                    <ChevronRight className="h-4 w-4 text-white/50" />
                  </button>
                ) : (
                  <div className="space-y-1.5">
                    {versionDist.map((v) => {
                      const isLatest = v.version && estate.catalogue_latest && v.version === estate.catalogue_latest;
                      const total = estate.device_count || 1;
                      const pct = Math.max(2, Math.round(((v.device_count || 0) / total) * 100));
                      return (
                        <button
                          key={v.version}
                          onClick={() => setDrillVersion(v.version)}
                          className="w-full flex items-center gap-3 py-1.5 hover:bg-white/5 rounded px-2 text-left"
                          data-testid={`drawer-version-${v.version}`}
                        >
                          <span className={`font-mono text-xs w-32 truncate ${isLatest ? 'text-green-400' : 'text-white/90'}`} title={v.version}>
                            {v.version} {isLatest && '★'}
                          </span>
                          <div className="flex-1 bg-slate-800 rounded-full h-2 overflow-hidden">
                            <div className={`h-full ${isLatest ? 'bg-green-500' : 'bg-cyan-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-white/70 text-xs tabular-nums w-16 text-right">
                            {v.device_count || 0}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {drillVersion && (
        <DeviceListModal
          family={family}
          version={drillVersion === '__all__' ? null : drillVersion}
          onClose={() => setDrillVersion(null)}
        />
      )}
      {canWriteOSPatch && showCustomUpload && (
        <CustomUploadModal
          family={family}
          onClose={() => setShowCustomUpload(false)}
          onUploaded={() => { fetchGov(); onUploaded?.(); }}
        />
      )}
    </>
  );
}

// ─────────────────────────── packaging status badge (drawer) ────────
function PackagingStatusBadge({ status }) {
  if (status === 'unsupported') return <Badge className="bg-slate-600 text-white text-xs">Unsupported</Badge>;
  if (status === 'live') return <Badge className="bg-purple-600 text-white text-xs">LIVE</Badge>;
  if (status === 'approved') return <Badge className="bg-green-600 text-white text-xs animate-pulse">Ready for Go Live</Badge>;
  if (status === 'deployed_test') return <Badge className="bg-orange-600 text-white text-xs">Deployed (Test)</Badge>;
  if (status === 'packaged') return <Badge className="bg-blue-600 text-white text-xs">Packaged</Badge>;
  if (status === 'packaging') return <Badge className="bg-sky-700 text-white text-xs">Packaging…</Badge>;
  return <Badge variant="outline" className="text-white/70 text-xs">Not packaged</Badge>;
}

// ─────────────────────────── compliance chart (collapsed) ──────────
const COMPLIANCE_COLORS = {
  on_latest: '#10B981', ahead_of_catalogue: '#0EA5E9', missing_one: '#F59E0B',
  missing_multiple: '#EF4444', unknown: '#8B5CF6', virtual: '#6B7280',
};
const STATUS_LABEL = {
  on_latest: 'On latest', ahead_of_catalogue: 'Ahead', missing_one: 'Missing 1',
  missing_multiple: 'Missing 2+', unknown: 'Unknown', virtual: 'Virtual',
  unsupported: 'Unsupported',
};
function ComplianceDonut({ row }) {
  const segments = useMemo(() => {
    const order = ['on_latest', 'ahead_of_catalogue', 'missing_one', 'missing_multiple', 'unknown'];
    const out = [];
    let cum = 0;
    const total = row.total || 1;
    for (const k of order) {
      const v = row.counts?.[k] || 0;
      if (!v) continue;
      const frac = v / total;
      out.push({ key: k, value: v, start: cum, end: cum + frac, color: COMPLIANCE_COLORS[k] || '#6B7280' });
      cum += frac;
    }
    return out;
  }, [row]);
  const R = 36;
  const C = 2 * Math.PI * R;
  return (
    <div className="flex flex-col items-center gap-2 p-3 rounded-lg bg-[rgba(229,239,250,0.05)] border border-[rgba(229,239,250,0.08)]">
      <div className="relative w-24 h-24">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r={R} fill="none" stroke="rgba(229,239,250,0.06)" strokeWidth="12" />
          {segments.map((s) => (
            <circle key={s.key} cx="50" cy="50" r={R} fill="none" stroke={s.color} strokeWidth="12"
              strokeDasharray={`${(s.end - s.start) * C} ${C}`}
              strokeDashoffset={`${-s.start * C}`}>
              <title>{`${STATUS_LABEL[s.key] || s.key}: ${s.value}`}</title>
            </circle>
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-xl font-semibold text-white tabular-nums leading-none">{row.compliance_pct}%</div>
          <div className="text-[10px] text-white/60 mt-0.5">compliant</div>
        </div>
      </div>
      <div className="text-sm text-white text-center max-w-[10rem]">
        <div className="font-medium truncate" title={row.manufacturer}>{row.manufacturer}</div>
        <div className="text-xs text-white/60">{row.total} device{row.total === 1 ? '' : 's'}</div>
      </div>
    </div>
  );
}
function ComplianceByManufacturer({ data }) {
  const [expanded, setExpanded] = useState(false);
  if (!data || data.length === 0) return null;
  return (
    <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]" data-testid="compliance-by-manufacturer">
      <CardHeader
        className={`border-[rgba(229,239,250,0.1)] cursor-pointer select-none hover:bg-[rgba(229,239,250,0.05)] transition-colors ${expanded ? 'border-b' : ''}`}
        onClick={() => setExpanded((v) => !v)}
        data-testid="compliance-by-manufacturer-toggle"
      >
        <CardTitle className="text-white flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-cyan-400" />
            Compliance score by manufacturer
            <span className="text-xs text-white/50 font-normal">({data.length} manufacturer{data.length === 1 ? '' : 's'})</span>
          </span>
          <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </CardTitle>
      </CardHeader>
      {expanded && (
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            {data.map((row) => <ComplianceDonut key={row.manufacturer} row={row} />)}
          </div>
          <div className="flex flex-wrap gap-3 mt-4 text-xs text-white/70">
            {['on_latest', 'ahead_of_catalogue', 'missing_one', 'missing_multiple', 'unknown'].map((k) => (
              <div key={k} className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: COMPLIANCE_COLORS[k] }} />
                <span>{STATUS_LABEL[k]}</span>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ─────────────────────────── bucket derivation (KPI tile filtering) ────
const VERSION_TUPLE_RE = /^(\d+)([A-Za-z]*)$/;
function _versionTuple(v) {
  if (!v) return [];
  return String(v).split(/[^A-Za-z0-9]+/).filter(Boolean).map((p) => {
    const m = p.match(VERSION_TUPLE_RE);
    return m ? [parseInt(m[1], 10), m[2] || ''] : [0, p];
  });
}
function _tupleCompare(a, b) {
  const n = Math.max(a.length, b.length);
  for (let i = 0; i < n; i++) {
    const [an, as] = a[i] || [0, ''];
    const [bn, bs] = b[i] || [0, ''];
    if (an !== bn) return an - bn;
    if (as !== bs) return as < bs ? -1 : 1;
  }
  return 0;
}
function familyBucket(f) {
  // Bug fix 2026-07-30: prefer the server-computed bucket so the
  // client filter agrees with the KPI card counts. Falls back to the
  // JS heuristic only for legacy family docs that don't carry the
  // ``bucket`` field yet.
  if (f && typeof f.bucket === 'string' && f.bucket) return f.bucket;
  const latest = (f.latest_available_version || '').trim();
  const versions = (f.versions_seen || []).map((v) => v.version).filter(Boolean);
  if (versions.length === 0) return 'unknown';
  if (!latest) return 'unknown';
  const latestT = _versionTuple(latest);
  const distinct = [...new Set(versions)];
  if (distinct.every((x) => _tupleCompare(_versionTuple(x), latestT) === 0)) return 'on_latest';
  if (distinct.some((x) => _tupleCompare(_versionTuple(x), latestT) > 0)) return 'ahead_of_catalogue';
  // Prefer catalog_versions history distance if provided.
  const catalog = f.catalog_versions || [];
  if (catalog.length > 0) {
    let maxDist = 0;
    for (const est of distinct) {
      const estT = _versionTuple(est);
      let found = false;
      for (let i = 0; i < catalog.length; i++) {
        if (_tupleCompare(_versionTuple(catalog[i]), estT) === 0) {
          maxDist = Math.max(maxDist, i);
          found = true;
          break;
        }
      }
      if (!found) maxDist = Math.max(maxDist, 2);
    }
    return maxDist <= 1 ? 'missing_one' : 'missing_multiple';
  }
  if (distinct.length === 1) return 'missing_one';
  return 'missing_multiple';
}

// ─────────────────────────── main component ────────────────────────
// 2026-08-06 — Package options modal. The Discover "Package" button used
// to fire the one-click endpoint immediately, silently taking the
// deferral/deadline defaults; it now surfaces the same PSADT deferral
// controls as the Deployments wizard before packaging the Test phase.
function PackageOptionsModal({ family, source, onClose, onConfirm }) {
  const useCustom = source === 'custom';
  const custom = family.custom_payload || {};
  const targetLabel = useCustom ? custom.target_version : family.latest_available_version;
  const toLocalInput = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const defaultAt = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    d.setHours(16, 0, 0, 0);
    return toLocalInput(d);
  };
  const [deferHours, setDeferHours] = useState(24);
  const [psadtDeadline, setPsadtDeadline] = useState(defaultAt(3));
  const [intuneDeadline, setIntuneDeadline] = useState(defaultAt(4));
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await onConfirm({
        source: useCustom ? 'custom' : 'catalogue',
        defer_interval_hours: parseInt(deferHours, 10) || 24,
        psadt_deadline_at: psadtDeadline
          ? (psadtDeadline.length === 16 ? `${psadtDeadline}:00Z` : psadtDeadline)
          : null,
        intune_deadline_at: intuneDeadline
          ? (intuneDeadline.length === 16 ? `${intuneDeadline}:00Z` : intuneDeadline)
          : null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="package-options-modal">
      <Card className="alice-popup w-full max-w-lg bg-slate-900 border-white/15">
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Rocket className="h-4 w-4 text-cyan-400" />
            Package {family.manufacturer} {family.model_family}
          </CardTitle>
          <p className="text-white/60 text-xs">
            Target: BIOS <span className="font-mono text-white/80">{targetLabel || 'latest'}</span>
            {useCustom && (
              <span className="text-blue-300" data-testid="package-options-custom-source">
                {' '}(custom {custom.source === 'url' ? 'download URL' : `upload: ${custom.filename || 'installer'}`})
              </span>
            )}
            {' '}— Test phase. End-users get PSADT&apos;s Install / Defer prompt (3 deferrals). The prompt stays
            on screen until it is answered; an unanswered prompt does not use a deferral. Once all deferrals are used,
            the user gets a final 60-minute countdown before the install starts.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wide">Defer spacing (hours)</Label>
            <Input
              type="number"
              min="1"
              max="168"
              value={deferHours}
              onChange={(e) => setDeferHours(e.target.value)}
              className="mt-1 bg-white/10 border-white/20 text-white text-sm h-9"
              data-testid="package-options-defer-interval"
            />
            <p className="text-white/50 text-[11px] mt-1">
              Default 24 h — minimum time between deferral prompts. Intune retries inside
              the window exit as Deferred without re-prompting or burning a deferral.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wide">PSADT deadline</Label>
              <Input
                type="datetime-local"
                value={psadtDeadline}
                onChange={(e) => setPsadtDeadline(e.target.value)}
                className="mt-1 bg-white/10 border-white/20 text-white text-sm h-9"
                data-testid="package-options-psadt-deadline"
              />
              <p className="text-white/50 text-[11px] mt-1">
                Shown to the user as &ldquo;Installation required by&rdquo;. Not enforced on the device:
                the deferral count and the final countdown decide when the install starts.
              </p>
            </div>
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wide">Intune backstop</Label>
              <Input
                type="datetime-local"
                value={intuneDeadline}
                onChange={(e) => setIntuneDeadline(e.target.value)}
                className="mt-1 bg-white/10 border-white/20 text-white text-sm h-9"
                data-testid="package-options-intune-backstop"
              />
              <p className="text-white/50 text-[11px] mt-1">
                Audit only — not sent to Graph. Intune&apos;s own check-in retry loop is the real backstop.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={submitting}
              className="text-white/80 hover:text-white"
              data-testid="package-options-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="package-options-confirm"
            >
              {submitting ? 'Packaging…' : 'Package & Deploy Test'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function FirmwareDiscoverTab({ onDeploySelection, lastIntuneSync, deviceSearch = null, onClearDeviceSearch = null }) {
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [manufacturerFilter, setManufacturerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [bucketFilter, setBucketFilter] = useState(''); // on_latest / ahead / missing_one / missing_multiple / unknown
  const [showOutOfScope, setShowOutOfScope] = useState(false);
  const [drillFamilyKey, setDrillFamilyKey] = useState(null); // opens Governance drawer
  const [packageModalFamily, setPackageModalFamily] = useState(null); // Package options modal
  const [packageSource, setPackageSource] = useState('catalogue'); // 'catalogue' | 'custom'
  const [configFamily, setConfigFamily] = useState(null);
  const [customUploadFamily, setCustomUploadFamily] = useState(null);
  // Read-only OS Patch tenants: firmware write modals never render.
  const { canWriteOSPatch } = usePermissions();

  // OEM catalogue + compliance-by-manufacturer state (migrated from FirmwareReportTab)
  const [catalogStatus, setCatalogStatus] = useState(null);
  const [syncingOem, setSyncingOem] = useState(null);
  const [summary, setSummary] = useState(null); // for by_manufacturer chart

  // `silent` reloads (after an action / while packaging runs) keep the
  // table on screen instead of flashing the loading state.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = {};
      if (searchText) params.search = searchText;
      if (manufacturerFilter) params.manufacturer = manufacturerFilter;
      if (statusFilter) params.packaging_status = statusFilter;
      if (!showOutOfScope) params.out_of_scope = false;
      const res = await axios.get(`${API}/firmware/families`, { params });
      setFamilies(res.data.families || []);
    } catch (e) {
      if (!silent) toast.error(`Failed to load families: ${e.response?.data?.detail || e.message}`);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [searchText, manufacturerFilter, statusFilter, showOutOfScope]);

  useEffect(() => { load(); }, [load]);

  // Load OEM catalogue status + summary (for compliance chart) + summary counts
  const [summaryCounts, setSummaryCounts] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [catRes, sumRes, cntRes] = await Promise.allSettled([
          axios.get(`${API}/firmware/catalog/sync-status`),
          axios.get(`${API}/firmware/summary`),
          axios.get(`${API}/firmware/families-summary-counts`),
        ]);
        if (cancelled) return;
        if (catRes.status === 'fulfilled') setCatalogStatus(catRes.value.data);
        if (sumRes.status === 'fulfilled') setSummary(sumRes.value.data);
        if (cntRes.status === 'fulfilled') setSummaryCounts(cntRes.value.data);
      } catch (err) {
        console.warn('Firmware header data load failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Refetch summary counts whenever families change (after refresh or bucket toggle)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/firmware/families-summary-counts`);
        if (!cancelled) setSummaryCounts(r.data);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [families.length]);

  // 2026-09-16 (Update 2) — refresh the page after Package, Go Live,
  // Next Phase, Custom Upload and the Auto toggle. The backend now
  // recomputes the family's status whenever a deployment is created or
  // changes status, so reloading the families + KPI cards is enough (no
  // full re-index). The staggered follow-ups pick up the first pipeline
  // transitions; the polling effect below takes over from there.
  const refreshTimers = React.useRef([]);
  const refreshNow = useCallback(async () => {
    await load({ silent: true });
    try {
      const r = await axios.get(`${API}/firmware/families-summary-counts`);
      setSummaryCounts(r.data);
    } catch (_) { /* silent */ }
  }, [load]);
  const refreshAfterAction = useCallback(() => {
    refreshTimers.current.forEach(clearTimeout);
    refreshNow();
    refreshTimers.current = [2000, 6000].map((ms) => setTimeout(refreshNow, ms));
  }, [refreshNow]);
  useEffect(() => () => refreshTimers.current.forEach(clearTimeout), []);

  // While any family has a deployment still being packaged / uploaded,
  // keep the rows moving without a manual refresh.
  const hasInFlight = families.some((f) => ['packaging', 'packaged'].includes(f.packaging_status?.status));
  useEffect(() => {
    if (!hasInFlight || drillFamilyKey) return undefined;
    const t = setInterval(refreshNow, 8000);
    return () => clearInterval(t);
  }, [hasInFlight, drillFamilyKey, refreshNow]);

  const rebuildIndex = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/firmware/families/refresh-index`);
      const { families: n, devices_scanned } = res.data.summary || {};
      toast.success(`Rebuilt ${n} families from ${devices_scanned} devices`);
      await load();
    } catch (e) {
      toast.error(`Refresh failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  const handleSyncCatalog = async (oem = null) => {
    setSyncingOem(oem || 'all');
    try {
      const url = oem ? `${API}/firmware/catalog/sync/${oem}` : `${API}/firmware/catalog/sync`;
      const res = await axios.post(url);
      const ingested = res.data?.total_versions_ingested ?? res.data?.versions_ingested ?? 0;
      toast.success(oem
        ? `${OEM_LABELS[oem] || oem} catalogue synced (${ingested} versions)`
        : `Catalogue synced (${ingested} versions across all OEMs)`);
      const [catRes, sumRes] = await Promise.allSettled([
        axios.get(`${API}/firmware/catalog/sync-status`),
        axios.get(`${API}/firmware/summary`),
      ]);
      if (catRes.status === 'fulfilled') setCatalogStatus(catRes.value.data);
      if (sumRes.status === 'fulfilled') setSummary(sumRes.value.data);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Catalogue sync failed');
    } finally {
      setSyncingOem(null);
    }
  };

  const patchFamily = async (family, patch) => {
    try {
      const res = await axios.patch(`${API}/firmware/families/${family.family_key}`, patch);
      setFamilies((prev) => prev.map((f) => f.family_key === family.family_key ? res.data : f));
      toast.success(
        'auto_update_enabled' in patch
          ? `Auto-update ${patch.auto_update_enabled ? 'enabled' : 'disabled'} for ${family.model_family}`
          : 'Family updated',
      );
      refreshAfterAction();
    } catch (e) {
      toast.error(`Update failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  // source: 'custom' packages the staged Custom Upload BIOS, 'catalogue'
  // the OEM catalogue head. Default: the staged custom BIOS when one is
  // waiting, otherwise the catalogue.
  const packageFamily = (family, source) => {
    // 2026-08-06 — open the options modal instead of firing the
    // one-click endpoint immediately (user report: Package skipped the
    // deferral/deadline options the Deployments wizard shows).
    setPackageSource(source || (hasStagedCustom(family) ? 'custom' : 'catalogue'));
    setPackageModalFamily(family);
  };

  const confirmPackageFamily = async (opts) => {
    const family = packageModalFamily;
    if (!family) return;
    try {
      const res = await axios.post(`${API}/firmware/families/${family.family_key}/package`, opts);
      toast.success(`Packaging queued (${res.data.deployment_id?.slice(0, 8)}…)`);
      setPackageModalFamily(null);
      refreshAfterAction();
    } catch (e) {
      toast.error(`Package failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const advancePhase = async (family) => {
    const deploymentId = family.packaging_status?.package_id;
    if (!deploymentId) { toast.error('No underlying deployment to advance.'); return; }
    try {
      const res = await axios.post(`${API}/firmware/deployments/${deploymentId}/advance-phase`, {});
      const dep = res.data?.deployment || {};
      if (dep.chain_completed) {
        toast.success('All devices already covered by the Test phase — deployment complete (LIVE).');
      } else if (dep.skipped_small_fleet) {
        toast.success(`Small fleet — skipped straight to Phase 3 (100%): ${dep.device_count} device(s) queued.`);
      } else {
        toast.success(`Advanced to ${dep.phase_slot || `Phase ${dep.phase}`} (${dep.device_count ?? '?'} devices). Pipeline queued.`);
      }
      refreshAfterAction();
    } catch (e) {
      toast.error(`Advance failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const goLive = async (family) => {
    const deploymentId = family.packaging_status?.package_id;
    if (!deploymentId) { toast.error('No underlying deployment for Go Live.'); return; }
    if (!window.confirm(
      `Go Live for ${family.manufacturer} ${family.model_family}?\n\n`
      + `ALICE calculates the devices for each phase (Phase 1 = 10%, Phase 2 = 50%, Phase 3 = 100%).\n`
      + `Small fleets (1-2 eligible devices) skip straight to Phase 3 and complete.`,
    )) return;
    await advancePhase(family);
  };

  const manufacturerOpts = useMemo(() => {
    const s = new Set(families.map((f) => f.manufacturer).filter(Boolean));
    return Array.from(s).sort();
  }, [families]);

  // Model-level KPI counts (recomputed client-side from `families`)
  const bucketCounts = useMemo(() => {
    const c = { on_latest: 0, ahead_of_catalogue: 0, missing_one: 0, missing_multiple: 0, unknown: 0 };
    for (const f of families) {
      if (f.out_of_scope || f.is_virtual || f.bucket === 'unsupported') continue;
      c[familyBucket(f)] = (c[familyBucket(f)] || 0) + 1;
    }
    return c;
  }, [families]);

  // Filter view by bucket (KPI tile click)
  const displayFamilies = useMemo(() => {
    if (!bucketFilter) return families;
    return families.filter((f) => familyBucket(f) === bucketFilter);
  }, [families, bucketFilter]);

  const oemStatuses = catalogStatus?.oems || [];

  // Task A (2026-07-30): family drilldown renders as an in-place detail
  // page (page-down pattern, same as Apps Management > Discover) instead
  // of the old dark slide-over. List state (filters, scroll, data) is
  // preserved in this component so Back restores the exact prior view.
  if (drillFamilyKey) {
    return (
      <div className="space-y-6" data-testid="firmware-discover-tab">
        <ModelFamilyDetailPage
          familyKey={drillFamilyKey}
          onBack={() => { setDrillFamilyKey(null); refreshAfterAction(); }}
          onDeploy={(fam, source) => { setDrillFamilyKey(null); packageFamily(fam, source); }}
          onUploaded={refreshAfterAction}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="firmware-discover-tab">
      {canWriteOSPatch && packageModalFamily && (
        <PackageOptionsModal
          family={packageModalFamily}
          source={packageSource}
          onClose={() => setPackageModalFamily(null)}
          onConfirm={confirmPackageFamily}
        />
      )}
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-white flex items-center gap-2">
            <Cpu className="h-6 w-6 text-cyan-400" />Firmware
          </h2>
          <p className="text-sm text-white/70 mt-1">
            One row per model family. Rebuild the index after a device sync or catalogue refresh to pick up new devices / BIOS versions.
          </p>
        </div>
        <Button
          onClick={rebuildIndex}
          disabled={refreshing}
          className="bg-cyan-600 hover:bg-cyan-700 text-white"
          data-testid="firmware-discover-refresh-index"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Rebuilding…' : 'Refresh index'}
        </Button>
      </div>

      {/* Row 1 — Total Devices / Ready to Automate / Total Models / Total Versions */}
      <div className="alice-kpi-row grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="firmware-top-cards">
        <TopStatCard
          label="Total Devices"
          value={summaryCounts?.total_devices ?? 0}
          subtitle="Supported devices for firmware (Virtual Machines / Cloud PCs excluded)"
          icon={HardDrive}
          accentColor="#00D4E4"
          testId="firmware-top-card-total-devices"
        />
        <TopStatCard
          label="Ready to Automate"
          value={`${summaryCounts?.ready_to_automate_pct ?? 0}%`}
          subtitle={`${(summaryCounts?.ready_to_automate_devices ?? 0).toLocaleString()} devices with firmware automation available`}
          icon={Package}
          accentColor="#22C55E"
          testId="firmware-top-card-ready-to-automate"
        />
        <TopStatCard
          label="Total Models"
          value={summaryCounts?.total_models ?? bucketCounts.on_latest + bucketCounts.ahead_of_catalogue + bucketCounts.missing_one + bucketCounts.missing_multiple + bucketCounts.unknown}
          subtitle="In-scope model families discovered across the estate"
          icon={Cpu}
          accentColor="#00D4E4"
          testId="firmware-top-card-total-models"
        />
        <TopStatCard
          label="Total Versions"
          value={summaryCounts?.total_versions ?? 0}
          subtitle="Distinct BIOS versions reported across all models"
          icon={Layers}
          accentColor="#8B5CF6"
          testId="firmware-top-card-total-versions"
        />
      </div>

      {/* Row 2 — Update Coverage / Managed Models / Families Needing Updates */}
      <div className="alice-kpi-row grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="firmware-top-cards-row2">
        <TopStatCard
          label="Overall Update Coverage"
          value={`${summaryCounts?.update_coverage_pct ?? 0}%`}
          subtitle={`${(summaryCounts?.devices_on_latest ?? 0).toLocaleString()} devices running the latest firmware`}
          icon={CheckCircle2}
          accentColor="#22C55E"
          testId="firmware-top-card-update-coverage"
        />
        <TopStatCard
          label="Managed Models"
          value={summaryCounts?.managed_models ?? 0}
          subtitle="Packaged or deployed through ALICE"
          icon={Rocket}
          accentColor="#8B5CF6"
          testId="firmware-top-card-managed-models"
        />
        <TopStatCard
          label="Families Needing Updates"
          value={summaryCounts?.families_needing_updates ?? 0}
          subtitle="Later version available and not managed through ALICE"
          icon={AlertTriangle}
          accentColor="#FC5442"
          testId="firmware-top-card-needing-updates"
        />
      </div>

      {/* Model-scoped KPI tiles — one row: Current / Missing 1 / Missing Multi / Unknown / Ahead */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="firmware-kpi-tiles">
        <button onClick={() => setBucketFilter(bucketFilter === 'on_latest' ? '' : 'on_latest')} className="text-left" data-testid="firmware-kpi-current">
          <ModelKPICard
            label="Current"
            families={summaryCounts?.counts?.on_latest?.families ?? bucketCounts.on_latest}
            devices={summaryCounts?.counts?.on_latest?.devices ?? 0}
            subtitle="On catalogue latest"
            icon={CheckCircle2} accentColor="#22C55E"
            active={bucketFilter === 'on_latest'}
            testId="kpi-tile-current"
          />
        </button>
        <button onClick={() => setBucketFilter(bucketFilter === 'missing_one' ? '' : 'missing_one')} className="text-left" data-testid="firmware-kpi-missing-one">
          <ModelKPICard
            label="Missing 1"
            families={summaryCounts?.counts?.missing_one?.families ?? bucketCounts.missing_one}
            devices={summaryCounts?.counts?.missing_one?.devices ?? 0}
            subtitle="One BIOS release behind"
            icon={AlertTriangle} accentColor="#00D4E4"
            active={bucketFilter === 'missing_one'}
            testId="kpi-tile-missing-one"
          />
        </button>
        <button onClick={() => setBucketFilter(bucketFilter === 'missing_multiple' ? '' : 'missing_multiple')} className="text-left" data-testid="firmware-kpi-missing-multi">
          <ModelKPICard
            label="Missing Multiple"
            families={summaryCounts?.counts?.missing_multiple?.families ?? bucketCounts.missing_multiple}
            devices={summaryCounts?.counts?.missing_multiple?.devices ?? 0}
            subtitle="More than one release behind"
            icon={Shield} accentColor="#FC5442"
            active={bucketFilter === 'missing_multiple'}
            testId="kpi-tile-missing-multiple"
          />
        </button>
        <button onClick={() => setBucketFilter(bucketFilter === 'unknown' ? '' : 'unknown')} className="text-left" data-testid="firmware-kpi-unknown">
          <ModelKPICard
            label="Unknown"
            families={summaryCounts?.counts?.unknown?.families ?? bucketCounts.unknown}
            devices={summaryCounts?.counts?.unknown?.devices ?? 0}
            subtitle="Not in catalogue or no BIOS reported"
            icon={HardDrive} accentColor="#8B5CF6"
            active={bucketFilter === 'unknown'}
            testId="kpi-tile-unknown"
          />
        </button>
        <button onClick={() => setBucketFilter(bucketFilter === 'ahead_of_catalogue' ? '' : 'ahead_of_catalogue')} className="text-left" data-testid="firmware-kpi-ahead">
          <ModelKPICard
            label="Ahead"
            families={summaryCounts?.counts?.ahead_of_catalogue?.families ?? bucketCounts.ahead_of_catalogue}
            devices={summaryCounts?.counts?.ahead_of_catalogue?.devices ?? 0}
            subtitle="Estate higher than catalogue — feed stale"
            icon={AlertTriangle} accentColor="#F59E0B"
            active={bucketFilter === 'ahead_of_catalogue'}
            testId="kpi-tile-ahead"
          />
        </button>
      </div>

      {bucketFilter && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-white/70">Filtered by bucket:</span>
          <Badge className="bg-cyan-600 text-white">{STATUS_LABEL[bucketFilter] || bucketFilter}</Badge>
          <button onClick={() => setBucketFilter('')} className="text-white/70 hover:text-white underline" data-testid="firmware-clear-bucket-filter">
            Clear filter
          </button>
        </div>
      )}

      {/* Compliance by Manufacturer moved to bottom (below Model families table) */}

      {/* Filter bar */}
      <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)]">
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search manufacturer or model…"
              className="w-full pl-9 pr-2 py-2 bg-[rgba(229,239,250,0.08)] border border-[rgba(229,239,250,0.15)] rounded text-sm text-white placeholder-white/40 focus:outline-none focus:border-cyan-400"
              data-testid="firmware-discover-search"
            />
          </div>
          <select
            value={manufacturerFilter}
            onChange={(e) => setManufacturerFilter(e.target.value)}
            className="px-2 py-2 bg-[rgba(229,239,250,0.08)] border border-[rgba(229,239,250,0.15)] rounded text-sm text-white"
            data-testid="firmware-discover-manufacturer-filter"
          >
            <option value="" className="bg-slate-900">All manufacturers</option>
            {manufacturerOpts.map((m) => <option key={m} value={m} className="bg-slate-900">{m}</option>)}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-2 py-2 bg-[rgba(229,239,250,0.08)] border border-[rgba(229,239,250,0.15)] rounded text-sm text-white"
            data-testid="firmware-discover-status-filter"
          >
            <option value="" className="bg-slate-900">All statuses</option>
            <option value="not_packaged" className="bg-slate-900">Not packaged</option>
            <option value="packaging" className="bg-slate-900">Packaging</option>
            <option value="packaged" className="bg-slate-900">Packaged</option>
            <option value="deployed_test" className="bg-slate-900">Deployed (test)</option>
            <option value="approved" className="bg-slate-900">Ready for Go Live</option>
            <option value="live" className="bg-slate-900">LIVE</option>
            <option value="unsupported" className="bg-slate-900">Unsupported</option>
          </select>
          <label className="flex items-center gap-1 text-sm text-white/80 cursor-pointer">
            <input
              type="checkbox"
              checked={showOutOfScope}
              onChange={(e) => setShowOutOfScope(e.target.checked)}
              className="accent-cyan-500"
              data-testid="firmware-discover-show-oos"
            />
            Show out of scope
          </label>
        </CardContent>
      </Card>

      {/* Family table — lighter blue Apps-Management styling */}
      <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
        <CardHeader className="p-4 border-b border-[rgba(229,239,250,0.1)]">
          <CardTitle className="text-white flex items-center justify-between">
            <span>Model families</span>
            <span className="text-sm text-white/60 font-normal">
              {displayFamilies.length} famil{displayFamilies.length === 1 ? 'y' : 'ies'}{bucketFilter ? ' matching filter' : ''}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-white/70" data-testid="firmware-discover-loading">Loading families…</div>
          ) : displayFamilies.length === 0 ? (
            <div className="p-8 text-center text-white/70" data-testid="firmware-discover-empty">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-amber-400" />
              {bucketFilter
                ? `No families in "${STATUS_LABEL[bucketFilter] || bucketFilter}" bucket.`
                : <>No families yet. Click <span className="font-semibold">Refresh index</span> to build the initial view from your device inventory.</>}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white/90 bg-[rgba(229,239,250,0.05)]">
                  <th className="py-3 px-4 font-semibold">Model Family</th>
                  <th className="py-3 px-4 font-semibold">Versions Seen</th>
                  <th className="py-3 px-4 font-semibold text-center">Status</th>
                  <th className="py-3 px-4 font-semibold text-right">Devices</th>
                  <th
                    className="py-3 px-4 font-semibold text-center"
                    title="Auto — when a newer BIOS appears in the OEM catalogue, ALICE packages it and deploys to this model's testers automatically. The phased rollout starts once testers approve."
                  >
                    Auto
                  </th>
                  <th className="py-3 px-4 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayFamilies.map((f, idx) => (
                  <tr
                    key={f.family_key}
                    className="border-t border-[rgba(229,239,250,0.08)] hover:bg-[rgba(229,239,250,0.08)] cursor-pointer transition-colors"
                    onClick={() => setDrillFamilyKey(f.family_key)}
                    data-testid={`firmware-family-row-${idx}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div>
                          <div className="text-white font-medium">
                            {f.manufacturer} <span className="text-white/80">{f.model_family}</span>
                          </div>
                          <div className="text-xs text-white/60">
                            {f.category && (
                              <Badge variant="outline" className="text-[10px] mr-1 border-white/20 text-white/80">
                                {f.category}
                              </Badge>
                            )}
                            Latest available: <span className="text-white/90">{f.latest_available_version || '—'}</span>
                            {!f.is_virtual && (f.latest_available_version || '').trim() && !(f.latest_download_url || '').trim() && (
                              <span
                                className="text-amber-400 ml-2 inline-flex items-center gap-1"
                                title={canWriteOSPatch
                                  ? 'The OEM catalogue lists this version but provides no BIOS download URL — use Custom Upload to package it'
                                  : 'The OEM catalogue lists this version but provides no BIOS download URL'}
                                data-testid={`firmware-no-url-hint-${idx}`}
                              >
                                <AlertTriangle className="h-3 w-3" />No download URL in OEM feed
                              </span>
                            )}
                            {f.latest_approved_version && (
                              <>{' · '}Approved: <span className="text-green-400">{f.latest_approved_version}</span></>
                            )}
                          </div>
                        </div>
                        {f.out_of_scope && (
                          <Badge variant="outline" className="text-xs text-slate-300 border-slate-500/50 bg-slate-700/30">
                            <EyeOff className="h-3 w-3 mr-1" />Out of Scope
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {(f.versions_seen || []).slice(0, 3).map((v, i) => (
                          <Badge key={i} variant="outline" className="text-xs text-white border-2 border-blue-900 bg-blue-950/40"
                            title={`${v.version} · ${v.device_count} device${v.device_count === 1 ? '' : 's'}`}
                          >
                            {v.version}
                          </Badge>
                        ))}
                        {(f.versions_seen || []).length > 3 && (
                          <Badge variant="outline" className="text-xs text-white/80 border-2 border-blue-900 bg-blue-950/40">
                            +{(f.versions_seen || []).length - 3}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                      <StatusBadge family={f} testId={`firmware-family-status-${idx}`} />
                    </td>
                    <td className="py-3 px-4 text-right text-white font-mono">
                      {f.device_count?.toLocaleString() || 0}
                    </td>
                    {/* Auto toggle — firmware mirror of the Apps Management
                        Packages auto-update switch (PackagesPage.jsx). */}
                    <td className="py-3 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                      <AutoUpdateToggle
                        family={f}
                        idx={idx}
                        onToggle={() => patchFamily(f, { auto_update_enabled: !f.auto_update_enabled })}
                      />
                    </td>
                    <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                      <NextActionButtons
                        family={f}
                        idx={idx}
                        onPackage={(source) => packageFamily(f, source)}
                        onCustomUpload={() => setCustomUploadFamily(f)}
                        onDeployTest={() => advancePhase(f)}
                        onGoLive={() => goLive(f)}
                        onAdvancePhase={() => advancePhase(f)}
                        onOutOfScope={() => patchFamily(f, { out_of_scope: !f.out_of_scope })}
                        onPhaseConfig={() => setConfigFamily(f)}
                        onDeploySelection={onDeploySelection}
                        onOpen={() => setDrillFamilyKey(f.family_key)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* OEM Catalogue strip — collapsed by default, moved to bottom */}
      <CollapsibleSection
        title={`OEM Catalogue — ${oemStatuses.length} source${oemStatuses.length === 1 ? '' : 's'}`}
        icon={HardDrive}
        testId="firmware-oem-catalogue-collapsible"
      >
        <div className="mb-3 flex justify-end">
          <Button
            onClick={() => handleSyncCatalog(null)}
            disabled={syncingOem !== null}
            className="bg-cyan-500 hover:bg-cyan-600 text-white text-sm"
            data-testid="firmware-sync-catalog-all-btn"
          >
            {syncingOem === 'all' ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Syncing…</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" />Sync catalogue now</>
            )}
          </Button>
        </div>
        <div className="alice-kpi-row grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="firmware-oem-strip">
          {oemStatuses.length === 0 && (
            <p className="text-white/70 text-sm col-span-5">
              Catalogue not yet synced. Click &ldquo;Sync catalogue now&rdquo; to fetch the first batch.
            </p>
          )}
          {oemStatuses.map((s) => (
            <div key={s.oem}
              className="p-3 rounded-lg border border-[rgba(229,239,250,0.1)] bg-[rgba(229,239,250,0.05)] flex flex-col gap-1"
              data-testid={`firmware-oem-card-${s.oem}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-white font-medium text-sm">{OEM_LABELS[s.oem] || s.oem}</span>
                <button
                  onClick={() => handleSyncCatalog(s.oem)}
                  disabled={syncingOem !== null}
                  className="text-cyan-400 hover:text-cyan-300 disabled:opacity-40"
                  data-testid={`firmware-oem-sync-btn-${s.oem}`}
                  title="Sync this OEM"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${syncingOem === s.oem ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <div className="text-white/70 text-xs">{s.model_count ?? 0} models · {s.version_count ?? 0} versions</div>
              <div className="text-white/60 text-xs">
                Last sync: {_timeAgo(s.last_sync_at)}
                {s.last_sync_status && s.last_sync_status !== 'success' && (
                  <span className="text-amber-400 ml-2">({s.last_sync_status})</span>
                )}
              </div>
              {s.last_error && (
                <div className="text-amber-300/80 text-xs truncate" title={s.last_error}>
                  Live feed unreachable — using curated seed
                </div>
              )}
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {/* Compliance by Manufacturer (collapsed by default) */}
      <ComplianceByManufacturer data={summary?.by_manufacturer || []} />

      {/* Device-level BIOS view — restored 2026-08 (collapsed by default).
          Same table as Reports → Firmware: filters, CSV export, per-device
          Package & Deploy. */}
      {/* `deviceSearch` comes from a Discover > Devices link for one device. */}
      <FirmwareDevicesSection
        collapsible
        defaultCollapsed
        deviceSearch={deviceSearch}
        onClearDeviceSearch={onClearDeviceSearch}
      />

      {canWriteOSPatch && configFamily && (
        <PhaseConfigModal
          family={configFamily}
          onClose={() => setConfigFamily(null)}
          onSaved={(updated) => setFamilies((prev) => prev.map((f) => f.family_key === updated.family_key ? updated : f))}
        />
      )}
      {canWriteOSPatch && customUploadFamily && (
        <CustomUploadModal
          family={customUploadFamily}
          onClose={() => setCustomUploadFamily(null)}
          onUploaded={refreshAfterAction}
        />
      )}
    </div>
  );
}

// ─────────────────────── auto-update toggle (per model) ──────────────
// 2026-08-13 — firmware mirror of the Apps Management Packages
// auto-update switch. ON = when the OEM catalogue publishes a newer
// BIOS for this model, ALICE creates the Test-phase deployment itself
// and the normal tester-approval → phased rollout takes over. OFF (the
// default) = firmware is only ever packaged when an admin asks.
function AutoUpdateToggle({ family, idx, onToggle }) {
  // Read-only OS Patch tenants see the On/Off state, not the switch.
  const { canWriteOSPatch } = usePermissions();
  const unsupported = family.is_virtual || family.packaging_status?.status === 'unsupported';
  if (unsupported) {
    return <span className="text-xs text-white/50" data-testid={`firmware-auto-toggle-na-${idx}`}>—</span>;
  }
  const on = Boolean(family.auto_update_enabled);
  const noTesters = (family.testers || []).length === 0;
  const title = on
    ? (noTesters
      ? 'Auto-update ENABLED, but no testers are configured for this model — add testers under Model Governance or ALICE cannot start the Test phase.'
      : `Auto-update enabled — ALICE deploys new OEM BIOS releases to this model's testers automatically${family.last_auto_update_at ? ` (last: ${family.auto_update_last_version || '—'})` : ''}.`)
    : 'Auto-update disabled — new BIOS releases for this model require the Package button.';
  return (
    <div className="inline-flex items-center gap-1.5">
      {canWriteOSPatch ? (
        <button
          onClick={onToggle}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${on ? 'bg-green-500' : 'bg-white/20'}`}
          title={title}
          data-testid={`firmware-auto-toggle-${idx}`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`}
          />
        </button>
      ) : (
        <span
          className={`text-xs font-medium ${on ? 'text-green-400' : 'text-white/60'}`}
          title={on ? 'Auto-update enabled for this model' : 'Auto-update disabled for this model'}
          data-testid={`firmware-auto-status-${idx}`}
        >
          {on ? 'On' : 'Off'}
        </span>
      )}
      {on && noTesters && (
        <AlertTriangle
          className="h-3.5 w-3.5 text-amber-400"
          data-testid={`firmware-auto-no-testers-${idx}`}
        />
      )}
    </div>
  );
}

// ─────────────────────────── row status badge ────────────────────────
function StatusBadge({ family, testId }) {
  const status = family.packaging_status?.status;
  if (family.is_virtual || status === 'unsupported') {
    return <Badge className="bg-slate-600 text-white text-xs" data-testid={testId}>Unsupported</Badge>;
  }
  if (status === 'live') {
    return <Badge className="bg-purple-600 text-white text-xs" data-testid={testId}><Rocket className="h-3 w-3 mr-1" />LIVE</Badge>;
  }
  if (status?.startsWith('live_phase')) {
    const phase = parseInt(status.replace('live_phase', ''), 10);
    return <Badge className="bg-blue-600 text-white text-xs" data-testid={testId}><Rocket className="h-3 w-3 mr-1" />LIVE — Phase {phase}</Badge>;
  }
  if (status === 'approved') {
    return <Badge className="bg-green-600 text-white text-xs animate-pulse" data-testid={testId}><CheckCircle2 className="h-3 w-3 mr-1" />Ready for Go Live</Badge>;
  }
  if (status === 'deployed_test') {
    return <Badge className="bg-orange-600 text-white text-xs" data-testid={testId}>Deployed (Test)</Badge>;
  }
  if (status === 'packaged') {
    return <Badge className="bg-blue-600 text-white text-xs" data-testid={testId}><Package className="h-3 w-3 mr-1" />Packaged</Badge>;
  }
  if (status === 'packaging') {
    return <Badge className="bg-sky-700 text-white text-xs" data-testid={testId}><RefreshCw className="h-3 w-3 mr-1 animate-spin" />Packaging…</Badge>;
  }
  return <Badge variant="outline" className="text-white/80 text-xs" data-testid={testId}>Not packaged</Badge>;
}

// ─────────────────────────── next-action buttons (per-status chain) ────
function NextActionButtons({
  family, idx, onPackage, onCustomUpload, onDeployTest, onGoLive,
  onAdvancePhase, onOutOfScope, onPhaseConfig, onDeploySelection, onOpen,
}) {
  // Read-only OS Patch tenants: every per-row write action is hidden; the
  // status badges and the Devices view button stay visible.
  const { canWriteOSPatch } = usePermissions();
  const status = family.packaging_status?.status;
  const currentPhase = family.packaging_status?.phase;

  // Virtual Machine / Cloud PC — unsupported, no packaging actions.
  // Devices can still be inspected via the drill-in detail page.
  if (family.is_virtual || status === 'unsupported') {
    return (
      <div className="flex gap-1 flex-wrap items-center">
        <Badge variant="outline" className="text-slate-300 border-slate-500/50 bg-slate-700/30 text-xs" data-testid={`firmware-unsupported-${idx}`}>
          Unsupported
        </Badge>
        <Button
          variant="outline" size="sm"
          onClick={() => onOpen?.()}
          className="bg-slate-700/40 text-white/80 border-white/20 hover:bg-slate-700/60 text-xs"
          data-testid={`firmware-view-devices-${idx}`}
          title="View the devices in this family (no firmware actions available)"
        >
          <Eye className="h-3 w-3 mr-1" />Devices
        </Button>
      </div>
    );
  }

  // 2026-09-16 (Bug 3) — a staged Custom Upload BIOS can be packaged from
  // any state (e.g. a newer custom BIOS for a model that is already LIVE).
  const stagedCustom = hasStagedCustom(family);
  const stagedVersion = family.custom_payload?.target_version;
  const packageCustomButton = canWriteOSPatch && stagedCustom ? (
    <Button
      variant="outline" size="sm"
      onClick={() => onPackage('custom')}
      className="bg-green-500 text-white border-green-600 hover:bg-green-600 font-semibold text-xs"
      data-testid={`firmware-package-custom-family-${idx}`}
      title={`Package the custom BIOS v${stagedVersion} (${family.custom_payload?.source === 'url' ? 'download URL' : 'uploaded file'}) and start the Test phase`}
    >
      <Package className="h-3 w-3 mr-1" />Package custom v{stagedVersion}
    </Button>
  ) : null;

  // Deployment queued / downloading / building — no actions until it lands.
  if (status === 'packaging') {
    return (
      <div className="flex gap-1 flex-wrap items-center">
        <Badge className="bg-sky-600/20 text-sky-300 border border-sky-500 text-xs" data-testid={`firmware-packaging-${idx}`}>
          <RefreshCw className="h-3 w-3 mr-1 animate-spin" />Packaging…
        </Badge>
      </div>
    );
  }

  // LIVE Phases (live_phase1 / live_phase2 / live_phase3) or fully LIVE
  if (status?.startsWith('live_phase') || status === 'live') {
    const phase = currentPhase || (status === 'live' ? 3 : parseInt(status.replace('live_phase', ''), 10));
    const isComplete = status === 'live' || phase >= 3;
    return (
      <div className="flex gap-1 flex-wrap items-center">
        <Badge className={`${isComplete ? 'bg-purple-600' : 'bg-blue-600'} text-white text-xs`}>
          <Rocket className="h-3 w-3 mr-1" />{isComplete ? 'LIVE' : `LIVE - Phase ${phase}`}
        </Badge>
        {packageCustomButton}
        {canWriteOSPatch && !isComplete && (
          <Button
            variant="outline" size="sm"
            onClick={() => onAdvancePhase()}
            className="bg-blue-500 text-white border-blue-600 hover:bg-blue-600 font-semibold text-xs"
            data-testid={`firmware-proceed-phase-${idx}`}
          >
            <ChevronRight className="h-3 w-3 mr-1" />Phase {phase + 1}
          </Button>
        )}
        {canWriteOSPatch && (
          <button
            onClick={onOutOfScope}
            className={`ml-1 p-1.5 rounded-md transition-all ${family.out_of_scope ? 'bg-slate-600/30 text-slate-300 hover:bg-cyan-600/30' : 'text-white/40 hover:text-slate-300 hover:bg-slate-600/20'}`}
            title={family.out_of_scope ? 'Mark In Scope' : 'Mark Out of Scope'}
            data-testid={`firmware-out-of-scope-${idx}`}
          >
            {family.out_of_scope ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          </button>
        )}
      </div>
    );
  }

  // Deployed to Test / Approved
  if (status === 'deployed_test' || status === 'approved') {
    const isApproved = status === 'approved';
    // doc.phase: 1 = test, 2 = phase1 (10%), 3 = phase2 (50%), 4 = phase3 (100%).
    const inPhases = (currentPhase || 1) >= 2;
    return (
      <div className="flex gap-1 flex-wrap">
        <Badge className={`${isApproved ? 'bg-green-600/20 text-green-400 border border-green-500' : 'bg-orange-600/20 text-orange-400 border border-orange-500'} text-xs`}>
          <CheckCircle2 className="h-3 w-3 mr-1" />{isApproved ? 'Approved' : (inPhases ? `Phase ${currentPhase - 1}` : 'Test')}
        </Badge>
        {canWriteOSPatch && (inPhases ? (
          <Button
            variant="outline" size="sm"
            onClick={onGoLive}
            className="bg-blue-500 text-white border-blue-600 hover:bg-blue-600 font-semibold text-xs"
            title="Progress this rollout to the next phase"
            data-testid={`firmware-nextphase-family-${idx}`}
          >
            <ChevronRight className="h-3 w-3 mr-1" />Next Phase
          </Button>
        ) : (
          <Button
            variant="outline" size="sm"
            onClick={onGoLive}
            className={`${isApproved ? 'bg-green-500 text-white border-green-600 hover:bg-green-600 font-semibold animate-pulse' : 'bg-purple-500 text-white border-purple-600 hover:bg-purple-600 font-semibold'} text-xs`}
            data-testid={`firmware-golive-family-${idx}`}
          >
            <Rocket className="h-3 w-3 mr-1" />{isApproved ? '🎉 Go Live Now!' : 'Go Live'}
          </Button>
        ))}
        {packageCustomButton}
      </div>
    );
  }

  // Packaged (not deployed)
  if (status === 'packaged') {
    return (
      <div className="flex gap-1 flex-wrap">
        <Badge className="bg-blue-600/20 text-blue-400 border border-blue-500 text-xs">
          <Package className="h-3 w-3 mr-1" />Packaged
        </Badge>
        {canWriteOSPatch && (
          <Button
            variant="outline" size="sm"
            onClick={onDeployTest}
            className="bg-orange-500 text-white border-orange-600 hover:bg-orange-600 font-semibold text-xs"
            data-testid={`firmware-deploy-test-family-${idx}`}
          >
            <Rocket className="h-3 w-3 mr-1" />Deploy Test
          </Button>
        )}
        {packageCustomButton}
      </div>
    );
  }

  // Not packaged — Package (staged custom BIOS, or catalogue latest +
  // download URL) or Custom Upload
  const hasCatalogueLatest = Boolean((family.latest_available_version || '').trim());
  const canPackageCatalogue = hasCatalogueLatest && Boolean((family.latest_download_url || '').trim());
  const canPackage = canPackageCatalogue || stagedCustom;
  if (!canWriteOSPatch) {
    // Only write actions live in this cell; keep the column aligned.
    return <span className="text-xs text-white/50" data-testid={`firmware-actions-readonly-${idx}`}>—</span>;
  }
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {stagedCustom ? packageCustomButton : canPackageCatalogue ? (
        <Button
          variant="outline" size="sm"
          onClick={() => onPackage('catalogue')}
          className="bg-green-500 text-white border-green-600 hover:bg-green-600 font-semibold text-xs"
          data-testid={`firmware-package-family-${idx}`}
          title={`Package BIOS v${family.latest_available_version} for all devices in this family`}
        >
          <Package className="h-3 w-3 mr-1" />Package
        </Button>
      ) : (
        <Button
          variant="outline" size="sm"
          onClick={onCustomUpload}
          className="bg-blue-500 text-white border-blue-600 hover:bg-blue-600 font-semibold text-xs"
          data-testid={`firmware-custom-upload-family-${idx}`}
        >
          <Upload className="h-3 w-3 mr-1" />Custom Upload
        </Button>
      )}
      {canPackage && (
        <Button
          variant="outline" size="sm"
          onClick={onCustomUpload}
          className="bg-slate-700/40 text-white border-white/20 hover:bg-slate-700/60 text-xs"
          data-testid={`firmware-custom-upload-alt-${idx}`}
          title={stagedCustom ? 'Replace the saved custom BIOS' : 'Upload a custom BIOS instead of using the catalogue'}
        >
          <Upload className="h-3 w-3 mr-1" />Custom
        </Button>
      )}
      <button
        onClick={onOutOfScope}
        className={`ml-1 p-1.5 rounded-md transition-all ${family.out_of_scope ? 'bg-slate-600/30 text-slate-300 hover:bg-cyan-600/30' : 'text-white/40 hover:text-slate-300 hover:bg-slate-600/20'}`}
        title={family.out_of_scope ? 'Mark In Scope' : 'Mark Out of Scope'}
        data-testid={`firmware-out-of-scope-${idx}`}
      >
        {family.out_of_scope ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
      </button>
      <button
        onClick={onPhaseConfig}
        className="p-1.5 rounded-md text-white/40 hover:text-white hover:bg-slate-600/20"
        title="Configure phase groups"
        data-testid={`firmware-phase-config-${idx}`}
      >
        <Settings2 className="h-4 w-4" />
      </button>
    </div>
  );
}
